'use client';

import { useTranslations, useFormatter } from 'next-intl';
import { Search, ChevronDown } from 'lucide-react';
import { type PaginatedResponse, type LeaderboardCity } from '@impact/types';
import { formatPublicCurrency } from '@/lib/format/currency-display';
import { Flag } from '@/components/ui/flag/flag';
import { RankBadge } from './RankBadge';

type SortBy = 'raised' | 'participants' | 'donations';

export function CityLeaderboardTable({
  data,
  isLoading,
  currency,
  sortBy,
  onSortChange,
}: {
  data: PaginatedResponse<LeaderboardCity> | undefined;
  isLoading: boolean;
  currency: string;
  sortBy: SortBy;
  onSortChange: (sort: SortBy) => void;
}) {
  const t = useTranslations('leaderboard');
  const format = useFormatter();

  if (isLoading) {
    return (
      <div className="divide-y divide-stone-100">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="p-4 md:px-6 md:py-4 animate-pulse">
            {/* Mobile skeleton */}
            <div className="flex items-center gap-3 md:hidden min-h-[60px]">
              <div className="w-8 h-8 rounded-full bg-stone-200 flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="h-4 w-28 bg-stone-200 rounded" />
                <div className="h-3 w-32 bg-stone-100 rounded" />
              </div>
              <div className="text-right space-y-1.5 flex-shrink-0">
                <div className="h-5 w-16 bg-stone-200 rounded ml-auto" />
                <div className="h-3 w-12 bg-stone-100 rounded ml-auto" />
              </div>
            </div>
            {/* Desktop skeleton */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <div className="col-span-1">
                <div className="w-8 h-8 rounded-full bg-stone-200" />
              </div>
              <div className="col-span-3">
                <div className="h-4 w-32 bg-stone-200 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-24 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-4 w-10 bg-stone-100 rounded" />
              </div>
              <div className="col-span-1">
                <div className="h-4 w-10 bg-stone-100 rounded" />
              </div>
              <div className="col-span-1">
                <div className="h-4 w-10 bg-stone-100 rounded" />
              </div>
              <div className="col-span-2">
                <div className="h-6 w-16 bg-stone-200 rounded ml-auto" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <div className="p-12 text-center">
        <Search className="w-12 h-12 text-stone-300 mx-auto mb-4" />
        <p className="text-stone-600">{t('noResults')}</p>
        <p className="text-stone-400 text-sm mt-1">{t('tryDifferentSearch')}</p>
      </div>
    );
  }

  const sortHeader = (column: SortBy, label: string) => {
    const isActive = sortBy === column;
    return (
      <button
        type="button"
        onClick={() => onSortChange(column)}
        className={`inline-flex items-center gap-1 text-right transition-colors ${isActive ? 'text-stone-700 font-semibold' : 'text-stone-500 hover:text-stone-700'}`}
      >
        {label}
        {isActive && <ChevronDown className="w-3 h-3" />}
      </button>
    );
  };

  return (
    <>
      {/* Mobile sort chips */}
      <div className="flex items-center gap-1.5 px-3 py-2.5 bg-stone-50 border-b border-stone-200 md:hidden">
        <span className="text-[11px] text-stone-400 uppercase tracking-wide">{t('sort')}</span>
        {(['raised', 'participants', 'donations'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => onSortChange(value)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
              sortBy === value
                ? 'bg-stone-800 text-white'
                : 'bg-white text-stone-500 border border-stone-300'
            }`}
          >
            {t(
              value === 'raised'
                ? 'sortRaised'
                : value === 'participants'
                  ? 'sortRiders'
                  : 'sortDonations',
            )}
          </button>
        ))}
      </div>

      {/* Desktop column headers */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-stone-50 text-stone-500 text-sm font-medium border-b border-stone-200">
        <div className="col-span-1">{t('colRank')}</div>
        <div className="col-span-3">{t('colCity')}</div>
        <div className="col-span-2">{t('colCountry')}</div>
        <div className="col-span-2 text-right">
          {sortHeader('participants', t('colParticipants'))}
        </div>
        <div className="col-span-1 text-right">{t('colTeams')}</div>
        <div className="col-span-1 text-right">{sortHeader('donations', t('colDonations'))}</div>
        <div className="col-span-2 text-right">{sortHeader('raised', t('colRaised'))}</div>
      </div>

      <div className="divide-y divide-stone-100">
        {items.map((city) => (
          <div key={city.id} className="px-4 py-[18px] md:px-6 md:py-4">
            {/* Mobile card layout */}
            <div className="md:hidden">
              <div className="flex items-center gap-3">
                <RankBadge rank={city.rank} />
                <div className="flex-1 min-w-0">
                  <p className="text-stone-900 font-medium text-[15px] truncate">{city.cityName}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <Flag code={city.countryCode} size="sm" />
                    <p className="text-stone-500 text-xs leading-snug truncate">
                      {city.countryName} · {city.participants} {t('colParticipants').toLowerCase()}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[17px] font-bold text-dgr-gold-hover">
                    {formatPublicCurrency(format, city.raised, currency)}
                  </p>
                  <p className="text-stone-500 text-xs mt-0.5">
                    {city.donations} {t('colDonations').toLowerCase()}
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop layout */}
            <div className="hidden md:grid md:grid-cols-12 md:gap-4 md:items-center">
              <div className="col-span-1 flex items-center gap-2">
                <RankBadge rank={city.rank} />
              </div>
              <div className="col-span-3">
                <p className="text-stone-900 font-medium truncate">{city.cityName}</p>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Flag code={city.countryCode} size="sm" />
                <p className="text-stone-600 text-sm">{city.countryName}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-stone-600">{city.participants}</p>
              </div>
              <div className="col-span-1 text-right">
                <p className="text-stone-600">{city.teams}</p>
              </div>
              <div className="col-span-1 text-right">
                <p className="text-stone-600">{city.donations}</p>
              </div>
              <div className="col-span-2 text-right">
                <p className="text-xl font-bold text-dgr-gold-hover">
                  {formatPublicCurrency(format, city.raised, currency)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
